/**
 * NutriKit Cookie Consent
 *
 * CNIL-compliant cookie consent manager. Replaces Shopify's native banner.
 *
 * Public API (window.NutriKitCookies):
 *   openSettings()  - Reopen the granular modal (for footer link "Gérer mes cookies")
 *   reset()         - Clear stored consent and re-show the banner (debug / user action)
 *   getConsent()    - Returns current consent object or null
 *
 * Storage: localStorage key "nutrikit-cookie-consent"
 *   {
 *     timestamp: ISO-8601 string,
 *     version: 1,
 *     analytics: boolean,
 *     marketing: boolean,
 *     functional: boolean
 *   }
 *
 * Shopify integration: calls window.Shopify.customerPrivacy.setTrackingConsent
 * so native Shopify pixels (analytics, marketing) respect the user's choice.
 */
(function () {
  'use strict';

  var STORAGE_KEY = 'nutrikit-cookie-consent';
  var STORAGE_VERSION = 1;

  var banner = null;
  var modal = null;
  var toggles = {};
  var lastFocused = null;

  function getStoredConsent() {
    try {
      var raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return null;
      var parsed = JSON.parse(raw);
      if (!parsed || parsed.version !== STORAGE_VERSION) return null;
      return parsed;
    } catch (e) {
      return null;
    }
  }

  function saveConsent(consent) {
    var record = {
      version: STORAGE_VERSION,
      timestamp: new Date().toISOString(),
      analytics: !!consent.analytics,
      marketing: !!consent.marketing,
      functional: !!consent.functional,
    };
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(record));
    } catch (e) {
      // localStorage blocked (private mode, quota). Consent still applied in-memory.
    }
    applyConsentToShopify(record);
    return record;
  }

  function applyConsentToShopify(consent) {
    // Shopify Customer Privacy API (loaded async by Shopify core).
    // If unavailable, queue the call so it runs when ready.
    var payload = {
      analytics: !!consent.analytics,
      marketing: !!consent.marketing,
      preferences: !!consent.functional,
      sale_of_data: !!consent.marketing,
    };

    function push() {
      try {
        if (
          window.Shopify &&
          window.Shopify.customerPrivacy &&
          typeof window.Shopify.customerPrivacy.setTrackingConsent === 'function'
        ) {
          window.Shopify.customerPrivacy.setTrackingConsent(payload, function () {});
          return true;
        }
      } catch (e) {}
      return false;
    }

    if (push()) return;

    // Retry briefly in case the API loads late.
    var attempts = 0;
    var interval = setInterval(function () {
      attempts++;
      if (push() || attempts > 20) {
        clearInterval(interval);
      }
    }, 250);
  }

  function showBanner() {
    if (!banner) return;
    banner.hidden = false;
  }

  function hideBanner() {
    if (!banner) return;
    banner.hidden = true;
  }

  function openModal(prefillFromStored) {
    if (!modal) return;
    lastFocused = document.activeElement;

    var stored = prefillFromStored ? getStoredConsent() : null;
    if (stored) {
      if (toggles.functional) toggles.functional.checked = !!stored.functional;
      if (toggles.analytics) toggles.analytics.checked = !!stored.analytics;
      if (toggles.marketing) toggles.marketing.checked = !!stored.marketing;
    }

    modal.hidden = false;
    document.body.style.overflow = 'hidden';
    var firstFocusable = modal.querySelector(
      'button, [href], input, [tabindex]:not([tabindex="-1"])'
    );
    if (firstFocusable) firstFocusable.focus();

    document.addEventListener('keydown', onKeydown);
  }

  function closeModal() {
    if (!modal) return;
    modal.hidden = true;
    document.body.style.overflow = '';
    document.removeEventListener('keydown', onKeydown);
    if (lastFocused && typeof lastFocused.focus === 'function') {
      lastFocused.focus();
    }
  }

  function onKeydown(event) {
    if (event.key === 'Escape') {
      closeModal();
    }
  }

  function acceptAll() {
    saveConsent({ analytics: true, marketing: true, functional: true });
    hideBanner();
    closeModal();
  }

  function declineAll() {
    saveConsent({ analytics: false, marketing: false, functional: false });
    hideBanner();
    closeModal();
  }

  function saveCustom() {
    saveConsent({
      analytics: toggles.analytics ? !!toggles.analytics.checked : false,
      marketing: toggles.marketing ? !!toggles.marketing.checked : false,
      functional: toggles.functional ? !!toggles.functional.checked : false,
    });
    hideBanner();
    closeModal();
  }

  function handleClick(event) {
    var target = event.target.closest('[data-cookie-action]');
    if (!target) return;
    var action = target.getAttribute('data-cookie-action');
    switch (action) {
      case 'accept':
        acceptAll();
        break;
      case 'decline':
        declineAll();
        break;
      case 'customize':
        openModal(true);
        break;
      case 'save':
        saveCustom();
        break;
      case 'close':
        closeModal();
        break;
    }
  }

  function init() {
    banner = document.getElementById('nutrikit-cookie-banner');
    modal = document.getElementById('nutrikit-cookie-modal');
    if (!banner || !modal) return;

    toggles.functional = modal.querySelector('[data-cookie-category="functional"]');
    toggles.analytics = modal.querySelector('[data-cookie-category="analytics"]');
    toggles.marketing = modal.querySelector('[data-cookie-category="marketing"]');

    banner.addEventListener('click', handleClick);
    modal.addEventListener('click', handleClick);

    var stored = getStoredConsent();
    if (stored) {
      // Consent already given: apply it silently (in case Shopify pixels load late).
      applyConsentToShopify(stored);
    } else {
      showBanner();
    }
  }

  // Public API
  window.NutriKitCookies = {
    openSettings: function () {
      hideBanner();
      openModal(true);
    },
    reset: function () {
      try {
        localStorage.removeItem(STORAGE_KEY);
      } catch (e) {}
      showBanner();
    },
    getConsent: function () {
      return getStoredConsent();
    },
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
